import { useMemo, useState, useEffect, useCallback } from 'react';

function useMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  const update = useCallback(() => setMobile(window.innerWidth < 768), []);
  useEffect(() => {
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);
  return mobile;
}
import { LineChart, Line, BarChart, Bar, LabelList, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { supabase } from './supabase.js';
import { curSym, nightCount, formatMoney } from './utils.js';
import { resolveTypeLabel } from './room-types.js';
import { localiseError } from './errors.js';
import {
  buildRoomNights,
  filterPeriod,
  sumRevenue,
  occupancyPct,
  adr,
  periodPresets,
  resolveSeasons,
  collapseSmallSources,
  paceCurve,
  hasPaceMeaning,
  seasonMonthRanges,
  bucketLos,
  LOS_BUCKETS,
  LT_BUCKETS,
} from './analytics-utils.js';
import MultiYearView from './MultiYearView.jsx';
import { panelStyle } from './theme.js';
import { PanelTitle } from './analytics-ui.jsx';

export default function Analytics({ property, rooms, t, lang, onBack, onMenuClick, onPropertyUpdate }) {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const seasons = useMemo(() => resolveSeasons(property, today), [property, today]);
  const [periodKey, setPeriodKey] = useState('thisSeason');
  const [breakdown, setBreakdown] = useState(() =>
    localStorage.getItem(`analytics:breakdown:${property.id}`) || 'type'
  );
  const [adrMode, setAdrMode] = useState(() =>
    localStorage.getItem(`analytics:adrMode:${property.id}`) || 'net'
  );
  useEffect(() => { localStorage.setItem(`analytics:breakdown:${property.id}`, breakdown); }, [breakdown, property.id]);
  useEffect(() => { localStorage.setItem(`analytics:adrMode:${property.id}`, adrMode); }, [adrMode, property.id]);

  const [subView, setSubView] = useState(() =>
    localStorage.getItem(`analytics:subView:${property.id}`) || 'season'
  );
  useEffect(() => { localStorage.setItem(`analytics:subView:${property.id}`, subView); }, [subView, property.id]);
  const [pastSeasons, setPastSeasons] = useState([]);

  useEffect(() => {
    const fromIso = isoAddYears(seasons.thisSeason.start, -5);
    const toIso   = isoAddYears(seasons.thisSeason.end,   +1);
    (async () => {
      setLoading(true);
      const [bk, ps] = await Promise.all([
        supabase
          .from('bookings')
          .select(`
            id, room_id, check_in, check_out, price, source, status,
            adults, children, breakfast_price, extra_beds, created_at,
            commission_pct_snapshot, vcc_fee_pct_snapshot, extra_bed_price_snapshot
          `)
          .eq('property_id', property.id)
          .gte('check_in', fromIso)
          .lte('check_in', toIso)
          .order('check_in'),
        supabase
          .from('past_seasons')
          .select('id, year, total_revenue, total_nights, months, source_split')
          .eq('property_id', property.id)
          .order('year'),
      ]);
      if (bk.error)        { setFetchError(localiseError(bk.error, lang)); }
      else if (ps.error)   { setFetchError(localiseError(ps.error, lang)); }
      else {
        setFetchError(null);
        setBookings(bk.data || []);
        setPastSeasons(ps.data || []);
      }
      setLoading(false);
    })();
  }, [property.id, seasons.thisSeason.start, seasons.thisSeason.end]);

  const refetchPastSeasons = useCallback(async () => {
    const { data, error } = await supabase
      .from('past_seasons')
      .select('id, year, total_revenue, total_nights, months, source_split')
      .eq('property_id', property.id)
      .order('year');
    if (!error) setPastSeasons(data || []);
  }, [property.id]);

  const roomNights = useMemo(
    () => buildRoomNights(bookings, rooms, property),
    [bookings, rooms, property],
  );

  const presets = useMemo(() => periodPresets(property, today), [property, today]);
  const period  = presets[periodKey] || presets.thisSeason;
  const periodNights = useMemo(
    () => filterPeriod(roomNights, period.start, period.end),
    [roomNights, period.start, period.end],
  );

  const periodBookingsByCheckIn = useMemo(
    () => bookings.filter(b => b.status === 'confirmed' && b.check_in >= period.start && b.check_in <= period.end),
    [bookings, period.start, period.end],
  );

  function onSeasonSave(startMd, endMd) {
    onPropertyUpdate({ season_start_md: startMd, season_end_md: endMd });
  }

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            display: 'inline-block', width: 8, height: 8,
            background: '#C9A96E', transform: 'rotate(45deg)',
            animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:0.3;transform:rotate(45deg) scale(0.8)} 50%{opacity:1;transform:rotate(45deg) scale(1.2)} }`}</style>
      <div style={{ fontFamily: "'Figtree', sans-serif", fontSize: 14, color: '#8B7D6B', textAlign: 'center' }}>
        Preparing your analytics…<br />
        <span style={{ fontSize: 12 }}>This can take a few seconds. Don't refresh.</span>
      </div>
    </div>
  );
  if (fetchError) return <div style={{ padding: 20, color: '#C0562B' }}>{fetchError}</div>;

  const segPillBtn = (on) => ({
    background: on ? '#3D2B1F' : 'transparent',
    color: on ? '#C9A96E' : '#8B7D6B',
    border: 'none', padding: '6px 14px',
    fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: "'Figtree', sans-serif", lineHeight: 1, height: '100%',
  });

  return (
    <div style={{ background: '#F5EFE6', minHeight: '100svh' }}>
      {/* Topbar — hamburger + ◆ TAVELO (matches the rest of the app chrome) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 12px', background: '#FFFDF8', position: 'sticky', top: 0, zIndex: 50, borderBottom: '1px solid #F0EBE1' }}>
        <button onClick={onMenuClick || onBack} aria-label="Menu" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, width: 22 }}>
            <span style={{ display: 'block', height: 1.5, background: '#3D2B1F', borderRadius: 1 }} />
            <span style={{ display: 'block', height: 1.5, background: '#3D2B1F', borderRadius: 1 }} />
            <span style={{ display: 'block', height: 1.5, background: '#3D2B1F', borderRadius: 1 }} />
          </span>
        </button>
        <button onClick={onBack} aria-label="Home" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: 12, letterSpacing: '0.22em', fontWeight: 600, color: '#3D2B1F', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 6, height: 6, background: '#C9A96E', transform: 'rotate(45deg)' }} />
          TAVELO
        </button>
        <span style={{ width: 22 }} />
      </div>

      {/* Cormorant page title */}
      <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 500, color: '#3D2B1F', margin: 0, padding: '14px 18px 6px', lineHeight: 1.1 }}>{t.analytics.tabLabel}</h1>

      {/* Sticky control row — Season/Multi-year toggle + period selector + season range */}
      <div style={{ position: 'sticky', top: 47, zIndex: 49, background: '#F5EFE6', padding: '8px 14px 12px', borderBottom: '1px solid #F0EBE1' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', border: '1px solid #E8D5B0', borderRadius: 999, overflow: 'hidden', background: '#FFFDF8', height: 28 }}>
            <button onClick={() => setSubView('season')} style={segPillBtn(subView === 'season')}>{t.analytics.multiYear.toggleSeason}</button>
            <button onClick={() => setSubView('multi')} style={segPillBtn(subView === 'multi')}>{t.analytics.multiYear.toggleMultiYear}</button>
          </span>
          {subView === 'season' && (
            <select value={periodKey} onChange={e => setPeriodKey(e.target.value)} style={{
              padding: '6px 28px 6px 10px', height: 28, borderRadius: 6, border: '1px solid #E8D5B0',
              background: '#FFFDF8', color: '#3D2B1F', fontSize: 12, fontFamily: "'Figtree', sans-serif",
              appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer',
              backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%238B7D6B\' stroke-width=\'2\'><polyline points=\'6 9 12 15 18 9\'/></svg>")',
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
            }}>
              <option value="thisSeason">{t.analytics.periods.thisSeason}</option>
              <option value="lastSeason">{t.analytics.periods.lastSeason}</option>
              <option value="thisMonth">{t.analytics.periods.thisMonth}</option>
              <option value="last30">{t.analytics.periods.last30}</option>
              <option value="ytd">{t.analytics.periods.ytd}</option>
            </select>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#8B7D6B', fontFamily: "'Figtree', sans-serif" }}>
            <SeasonLink property={property} t={t} lang={lang} onSave={onSeasonSave} />
          </span>
        </div>
        {subView === 'season' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <select value={breakdown} onChange={e => setBreakdown(e.target.value)} style={{
              padding: '5px 24px 5px 10px', height: 24, borderRadius: 6, border: '1px solid #E8D5B0',
              background: '#FFFDF8', color: '#3D2B1F', fontSize: 11, fontFamily: "'Figtree', sans-serif",
              appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer',
              backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%238B7D6B\' stroke-width=\'2\'><polyline points=\'6 9 12 15 18 9\'/></svg>")',
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center',
            }}>
              <option value="type">{t.analytics.breakdown.perRoomType}</option>
              <option value="room">{t.analytics.breakdown.perRoom}</option>
            </select>
          </div>
        )}
      </div>

      <div style={{ padding: '12px 14px 32px' }}>
        {subView === 'season' ? (
          <>
            <KpiCards roomNights={periodNights} rooms={rooms} period={period} t={t} cur={curSym(property.currency)} adrMode={adrMode} setAdrMode={setAdrMode} lang={lang} />
            <PaceRow roomNights={roomNights} property={property} rooms={rooms} seasons={seasons} today={today} t={t} lang={lang} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, marginBottom: 12 }}>
              <PerLevelTable periodNights={periodNights} rooms={rooms} period={period} breakdown={breakdown} adrMode={adrMode} t={t} cur={curSym(property.currency)} lang={lang} roomTypeInfo={property.room_type_info} />
              <WeekdayWeekend periodNights={periodNights} rooms={rooms} period={period} t={t} />
            </div>
            <RevenueBySource periodNights={periodNights} t={t} cur={curSym(property.currency)} />
            <RevenueSeries periodNights={periodNights} period={period} t={t} cur={curSym(property.currency)} lang={lang} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
              <LosPanel bookings={periodBookingsByCheckIn} t={t} lang={lang} />
              <LeadTimeByMonth bookings={periodBookingsByCheckIn} t={t} lang={lang} />
            </div>
          </>
        ) : (
          <MultiYearView
            property={property}
            rooms={rooms}
            bookings={bookings}
            pastSeasons={pastSeasons}
            seasons={seasons}
            today={today}
            t={t}
            lang={lang}
            onBackfillSaved={refetchPastSeasons}
          />
        )}
      </div>
    </div>
  );
}

function SeasonLink({ property, t, lang, onSave }) {
  const [open, setOpen] = useState(false);
  const label = formatSeason(property.season_start_md, property.season_end_md, lang);
  return (
    <>
      <button onClick={() => setOpen(true)} style={{ background: 'none', border: 'none', color: '#8B7D6B', cursor: 'pointer', fontSize: 13 }}>
        {t.analytics.season.label}: {label} · {t.analytics.season.edit}
      </button>
      {open && <SeasonModal property={property} t={t} lang={lang} onCancel={() => setOpen(false)} onSaved={(s, e) => { setOpen(false); onSave(s, e); }} />}
    </>
  );
}

function SeasonModal({ property, t, lang, onCancel, onSaved }) {
  const [startMd, setStartMd] = useState(property.season_start_md || '04-01');
  const [endMd,   setEndMd]   = useState(property.season_end_md   || '10-31');
  const [err, setErr] = useState(null);

  async function save() {
    if (!isRealMd(startMd) || !isRealMd(endMd)) {
      setErr(t.analytics.season.invalid);
      return;
    }
    const { error } = await supabase.from('properties')
      .update({ season_start_md: startMd, season_end_md: endMd })
      .eq('id', property.id);
    if (error) { setErr(localiseError(error, lang)); return; }
    onSaved(startMd, endMd);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#FFFDF8', padding: 24, borderRadius: 12, minWidth: 320, maxWidth: 420 }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, margin: 0, marginBottom: 12, color: '#3D2B1F', lineHeight: 1.1 }}>{t.analytics.season.editTitle}</h2>
        <p style={{ fontSize: 13, color: '#8B7D6B', marginTop: 0 }}>{t.analytics.season.helpText}</p>
        <label style={fieldLbl}>{t.analytics.season.start}</label>
        <MonthDayPicker value={startMd} onChange={setStartMd} lang={lang} />
        <label style={fieldLbl}>{t.analytics.season.end}</label>
        <MonthDayPicker value={endMd} onChange={setEndMd} lang={lang} />
        {err && <p style={{ color: '#C0562B', fontSize: 13 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel} style={btnSecondary}>{t.analytics.season.cancel}</button>
          <button onClick={save} style={btnPrimary}>{t.analytics.season.save}</button>
        </div>
      </div>
    </div>
  );
}

function MonthDayPicker({ value, onChange, lang }) {
  const [m, d] = value.split('-');
  const months = lang === 'bg'
    ? ['ян','фев','мар','апр','май','юни','юли','авг','сеп','окт','ное','дек']
    : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const maxDay = new Date(2001, Number(m), 0).getDate();
  const day = Math.min(Number(d), maxDay);
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <select value={m} onChange={e => onChange(`${e.target.value}-${String(day).padStart(2, '0')}`)} style={selectStyle}>
        {months.map((name, i) => {
          const mm = String(i + 1).padStart(2, '0');
          return <option key={mm} value={mm}>{name}</option>;
        })}
      </select>
      <input
        type="number" min="1" max={maxDay} value={day}
        onChange={e => onChange(`${m}-${String(Math.min(maxDay, Math.max(1, Number(e.target.value) || 1))).padStart(2, '0')}`)}
        style={{ ...selectStyle, width: 64 }}
      />
    </div>
  );
}

function bucketPeriod(periodNights, period) {
  const days = daysInclusive(period.start, period.end);
  const granularity = days <= 31 ? 'day' : days <= 120 ? 'week' : 'month';

  const buckets = new Map();
  for (const n of periodNights) {
    let key;
    if      (granularity === 'day')   key = n.date;
    else if (granularity === 'week')  key = weekKey(n.date);
    else                              key = n.date.slice(0, 7);
    if (!buckets.has(key)) buckets.set(key, { bucket: key, gross: 0, net: 0, ota: 0 });
    const b = buckets.get(key);
    b.gross += n.nightlyGross;
    b.net   += n.nightlyNet;
    b.ota   += n.nightlyOtaCost ?? 0;
  }
  return { data: Array.from(buckets.values()).sort((a, b) => a.bucket < b.bucket ? -1 : 1), granularity };
}

function weekKey(iso) {
  const d = new Date(iso + 'T12:00:00Z');
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 1 - day);
  return d.toISOString().slice(0, 10);
}

function isoAddDays(iso, n) {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function maxIso(a, b) { return a > b ? a : b; }
function monthLabel(iso, lang = 'en') {
  const [y, m] = iso.split('-').map(Number);
  const names = MONTH_SHORT[lang] || MONTH_SHORT.en;
  return `${names[m - 1]} ${y}`;
}

const MONTH_SHORT = {
  bg: ['Яну','Фев','Мар','Апр','Май','Юни','Юли','Авг','Сеп','Окт','Ное','Дек'],
  en: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
};

function WeekdayWeekend({ periodNights, rooms, period, t }) {
  // Fri (5) + Sat (6) = peak; everything else = off-peak
  let peakNights = 0, offNights = 0, peakDays = 0, offDays = 0;

  let cursor = period.start;
  while (cursor <= period.end) {
    const dow = new Date(cursor + 'T12:00:00Z').getUTCDay();
    if (dow === 5 || dow === 6) peakDays++; else offDays++;
    cursor = isoAddDays(cursor, 1);
  }

  for (const n of periodNights) {
    const dow = new Date(n.date + 'T12:00:00Z').getUTCDay();
    if (dow === 5 || dow === 6) peakNights++; else offNights++;
  }

  const cap = rooms.length;
  const peakOcc  = cap > 0 && peakDays > 0  ? (peakNights  / (cap * peakDays))  * 100 : null;
  const offOcc   = cap > 0 && offDays  > 0  ? (offNights   / (cap * offDays))   * 100 : null;

  const rows = [
    { label: t.analytics.weekdayWeekend.peak,    occ: peakOcc,  color: '#C9A96E' },
    { label: t.analytics.weekdayWeekend.offPeak, occ: offOcc,   color: '#8B7D6B' },
  ];

  return (
    <div style={panelStyle}>
      <PanelTitle>{t.analytics.weekdayWeekend.title}</PanelTitle>
      <p style={{ fontSize: 11, color: '#8B7D6B', margin: '0 0 12px' }}>{t.analytics.weekdayWeekend.note}</p>
      {rows.map(r => (
        <div key={r.label} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span style={{ color: '#3D2B1F', fontWeight: 600 }}>{r.label}</span>
            <span style={{ color: r.color, fontWeight: 700 }}>{r.occ == null ? '—' : `${r.occ.toFixed(1)}%`}</span>
          </div>
          <div style={{ height: 8, background: '#F5EFE6', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(r.occ || 0, 100)}%`, background: r.color, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyPanel({ text }) {
  return <div style={{ ...panelStyle, textAlign: 'center', color: '#8B7D6B', padding: 40 }}>{text}</div>;
}

function RevenueSeries({ periodNights, period, t, cur, lang }) {
  const buckets = new Map();
  for (const n of periodNights) {
    if (!buckets.has(n.date)) buckets.set(n.date, { bucket: n.date, net: 0 });
    buckets.get(n.date).net += n.nightlyNet;
  }
  const data = Array.from(buckets.values()).sort((a, b) => a.bucket < b.bucket ? -1 : 1);

  if (data.length === 0) return <EmptyPanel text={t.analytics.empty} />;

  const avg = data.reduce((s, d) => s + d.net, 0) / data.length;
  const months = MONTH_SHORT[lang] || MONTH_SHORT.en;
  const ticks = data.filter((_, i) => i % 14 === 0).map(d => d.bucket);

  return (
    <div style={panelStyle}>
      <PanelTitle>{t.analytics.kpi.revenue}</PanelTitle>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <XAxis dataKey="bucket" stroke="#8B7D6B" tick={{ fontSize: 10 }}
            ticks={ticks}
            tickFormatter={v => {
              const d = new Date(v + 'T12:00:00Z');
              return months[d.getUTCMonth()] + ' ' + d.getUTCDate();
            }}
          />
          <YAxis stroke="#8B7D6B" tick={{ fontSize: 11 }}
            tickFormatter={v => `${cur}${Math.round(v).toLocaleString('en-US')}`}
          />
          <Tooltip
            formatter={v => [`${cur}${Math.round(v).toLocaleString()}`, t.analytics.kpi.revenue]}
            labelFormatter={v => {
              const d = new Date(v + 'T12:00:00Z');
              return months[d.getUTCMonth()] + ' ' + d.getUTCDate();
            }}
          />
          <ReferenceLine y={avg} stroke="#C9A96E" strokeDasharray="4 3" strokeWidth={1.5} />
          <Line type="monotone" dataKey="net" stroke="#5A7247" dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function RevenueBySource({ periodNights, t, cur }) {
  const SOURCE_NORM = {
    'booking.com': 'Booking.com',
    'direct': 'Direct', 'Директно': 'Direct', 'Директен': 'Direct',
    'phone': 'Phone', 'Телефон': 'Phone', 'Телефонен': 'Phone',
  };
  const norm = s => SOURCE_NORM[s] || SOURCE_NORM[s?.toLowerCase()] || s;

  const bySource = {};
  for (const n of periodNights) {
    bySource[norm(n.source)] = (bySource[norm(n.source)] || 0) + n.nightlyGross;
  }

  const bySourceOta = {};
  for (const n of periodNights) {
    const src = norm(n.source);
    bySourceOta[src] = (bySourceOta[src] || 0) + (n.nightlyOtaCost ?? 0);
  }

  const collapsed = collapseSmallSources(bySource);
  const entries = Object.entries(collapsed).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) return <EmptyPanel text={t.analytics.empty} />;

  const palette = { 'Booking.com': '#2B6F8A', 'Direct': '#C9A96E', 'Phone': '#5A7247', 'Other': '#8B7D6B' };

  const otaEntries   = entries.filter(([src]) => (bySourceOta[src] || 0) > 0);
  const otherEntries = entries.filter(([src]) => (bySourceOta[src] || 0) === 0);

  return (
    <div style={panelStyle}>
      <PanelTitle>{t.analytics.revenueBySource}</PanelTitle>
      <div style={{ display: 'flex', height: 32, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
        {otaEntries.length > 0 && (
          <div style={{ position: 'relative', display: 'flex', flexGrow: otaEntries.reduce((s, [, v]) => s + v, 0), overflow: 'hidden', borderRadius: 2 }}>
            {otaEntries.map(([src, val]) => (
              <div key={src}
                   style={{ background: palette[src] || '#8B7D6B', flexGrow: val, minWidth: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {Math.round(val / total * 100) >= 8 && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                    {Math.round(val / total * 100)}%
                  </span>
                )}
              </div>
            ))}
            <div style={{ position: 'absolute', inset: 0, border: '2px dashed #C0562B', borderRadius: 2, pointerEvents: 'none' }} />
          </div>
        )}
        {otherEntries.map(([src, val]) => (
          <div key={src}
               style={{ background: palette[src] || '#8B7D6B', flexGrow: val, minWidth: 2, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {Math.round(val / total * 100) >= 8 && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                {Math.round(val / total * 100)}%
              </span>
            )}
          </div>
        ))}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 12 }}>
        {[...otaEntries, ...otherEntries].map(([src, val]) => {
          const isOta = (bySourceOta[src] || 0) > 0;
          return (
            <li key={src} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ background: palette[src] || '#8B7D6B', width: 10, height: 10, borderRadius: 2, display: 'inline-block', outline: isOta ? '1px dashed #C0562B' : 'none' }} />
              <span style={{ color: '#3D2B1F', flex: 1 }}>{src === 'Other' ? t.analytics.other : (t.analytics.sources?.[src] || src)}</span>
              <span style={{ color: '#8B7D6B' }}>{cur}{Math.round(val).toLocaleString()} · {Math.round(val / total * 100)}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PaceRow({ roomNights, property, rooms, seasons, today, t, lang }) {
  // Match the calendar: active rooms only, and only their room-nights.
  const activeRooms = rooms.filter(r => !r.archived);
  const activeIds = new Set(activeRooms.map(r => r.id));
  const activeNights = roomNights.filter(n => activeIds.has(n.roomId));
  const months = seasonMonthRanges(seasons.thisSeason)
    .map(m => ({ ...m, label: monthLabel(m.start, lang) }));
  if (months.length === 0 || activeRooms.length === 0) return null;

  return (
    <div style={{ ...panelStyle, marginBottom: 20 }}>
      <PanelTitle>{t.analytics.pace.title}</PanelTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 12 }}>
        {months.map(m => <PaceChart key={m.label} roomNights={activeNights} month={m} rooms={activeRooms} seasons={seasons} today={today} t={t} />)}
      </div>
    </div>
  );
}

function PaceChart({ roomNights, month, rooms, seasons, today, t }) {
  const target = { start: month.start, end: month.end };
  const daysInMonth = daysInclusive(target.start, target.end);
  const totalRoomNights = rooms.length * daysInMonth;
  const sweepStart = maxIso(isoAddDays(target.start, -7 * 12), seasons.thisSeason.start);
  const curve = paceCurve(roomNights, target, sweepStart, today, totalRoomNights);
  // Headline = occupancy as of today (every booking has createdAt <= today), so it
  // matches the calendar's month occupancy rather than the last weekly snapshot.
  const onBooksToday = roomNights.filter(n => n.date >= target.start && n.date <= target.end).length;
  const currentPct = totalRoomNights > 0 ? (onBooksToday / totalRoomNights) * 100 : 0;
  const showLine = hasPaceMeaning(curve);
  const bookedLabel = t.analytics.pace.booked.replace('{pct}% ', '').replace('{pct}%', '').trim();

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8B7D6B', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{month.label}</div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 500, color: '#3D2B1F', lineHeight: 1, marginBottom: 2, fontVariantNumeric: 'lining-nums tabular-nums' }}>
        {currentPct.toFixed(0)}%
      </div>
      <div style={{ fontSize: 11, color: '#8B7D6B', marginBottom: 6 }}>{bookedLabel}</div>
      <div style={{ height: 5, background: '#F5EFE6', borderRadius: 3, overflow: 'hidden', marginBottom: showLine ? 8 : 0 }}>
        <div style={{ height: '100%', width: `${Math.min(currentPct, 100)}%`, background: '#5A7247', borderRadius: 3 }} />
      </div>
      {showLine && (
        <ResponsiveContainer width="100%" height={50}>
          <LineChart data={curve}>
            <XAxis dataKey="date" hide />
            <YAxis domain={[0, 100]} hide />
            <Line type="monotone" dataKey="pct" stroke="#5A7247" dot={false} strokeWidth={1.5} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const LOS_COLORS = ['#5A7247', '#C9A96E', '#2B6F8A', '#8B7D6B'];

const RADIAN = Math.PI / 180;

function LosPanel({ bookings, t, lang }) {
  const mobile = useMobile();
  const counts = bucketLos(bookings);
  const { meanCount, meanSum } = bookings.reduce(
    (acc, b) => { const n = nightCount(b.check_in, b.check_out); if (n > 0) { acc.meanCount += 1; acc.meanSum += n; } return acc; },
    { meanCount: 0, meanSum: 0 },
  );
  const meanNights = meanCount > 0 ? (meanSum / meanCount).toFixed(1) : '—';

  const pieData = LOS_BUCKETS
    .map((k, i) => ({ bucket: k, count: counts[k] || 0, color: LOS_COLORS[i] }))
    .filter(d => d.count > 0);
  const pieTotal = pieData.reduce((s, d) => s + d.count, 0);

  const byMonth = new Map();
  for (const b of bookings) {
    const key = b.check_in.slice(0, 7);
    const n = nightCount(b.check_in, b.check_out);
    if (n <= 0) continue;
    if (!byMonth.has(key)) byMonth.set(key, { sum: 0, count: 0 });
    const m = byMonth.get(key);
    m.sum += n;
    m.count += 1;
  }
  const monthNames = MONTH_SHORT[lang] || MONTH_SHORT.en;
  const barData = Array.from(byMonth.entries())
    .sort(([a], [b]) => a < b ? -1 : 1)
    .map(([key, { sum, count }]) => ({
      month: monthNames[parseInt(key.slice(5, 7)) - 1],
      avg: parseFloat((sum / count).toFixed(1)),
      count,
    }));

  if (pieTotal === 0 && barData.length === 0) return <EmptyPanel text={t.analytics.empty} />;

  return (
    <div style={panelStyle}>
      <PanelTitle>{t.analytics.los.title}</PanelTitle>
      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: 0 }}>
        {/* Donut + legend */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingRight: mobile ? 0 : 12, paddingBottom: mobile ? 16 : 0 }}>
          {pieTotal > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 130, height: 130, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="count"
                        innerRadius={33}
                        outerRadius={60}
                        paddingAngle={2}
                        label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                          if (percent < 0.07) return null;
                          const r = innerRadius + (outerRadius - innerRadius) * 0.55;
                          const x = cx + r * Math.cos(-midAngle * RADIAN);
                          const y = cy + r * Math.sin(-midAngle * RADIAN);
                          return (
                            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700}>
                              {Math.round(percent * 100)}%
                            </text>
                          );
                        }}
                        labelLine={false}
                      >
                        {pieData.map(d => <Cell key={d.bucket} fill={d.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, _, props) => [`${v} (${Math.round(v / pieTotal * 100)}%)`, props.payload.bucket]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ flex: 1, fontSize: 12 }}>
                  {pieData.map(d => (
                    <div key={d.bucket} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ background: d.color, width: 10, height: 10, borderRadius: 2, flexShrink: 0 }} />
                      <span style={{ color: '#3D2B1F', flex: 1 }}>{d.bucket} {t.analytics.los.nightsUnit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        {/* Divider */}
        {!mobile && <div style={{ width: 1, background: '#3D2B1F', opacity: 0.15, flexShrink: 0 }} />}
        {mobile && barData.length > 0 && <div style={{ height: 1, background: '#3D2B1F', opacity: 0.15 }} />}
        {/* Bar by month */}
        <div style={{ flex: 2, paddingLeft: mobile ? 0 : 12, paddingTop: mobile ? 14 : 0 }}>
          {barData.length > 0 && (
            <>
              <ResponsiveContainer width="100%" height={185}>
                <BarChart data={barData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" stroke="#8B7D6B" tick={{ fontSize: 11, fill: '#8B7D6B' }} />
                  <YAxis stroke="#8B7D6B" tick={{ fontSize: 11 }} allowDecimals unit={t.analytics.los.nightShort} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: '#FFFDF8', border: '1px solid #E8D5B0', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                          <div style={{ fontWeight: 700, color: '#3D2B1F', marginBottom: 4 }}>{d.month}</div>
                          <div style={{ color: '#5A7247' }}>{t.analytics.los.avgStay}: {d.avg} {t.analytics.los.nightsUnit}</div>
                          <div style={{ color: '#8B7D6B' }}>{d.count} {t.analytics.los.reservations}</div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="avg" fill="#5A7247" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="avg" position="top" style={{ fontSize: 11, fill: '#3D2B1F', fontWeight: 600 }} formatter={v => `${v}${t.analytics.los.nightShort}`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p style={{ fontSize: 12, color: '#8B7D6B', margin: '4px 0 0' }}>
                {t.analytics.los.mean.replace('{n}', meanNights)}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LeadTimeByMonth({ bookings, t, lang }) {
  const byMonth = new Map();
  for (const b of bookings) {
    if (!b.created_at) continue;
    const monthKey = b.check_in.slice(0, 7);
    const lead = Math.round(
      (new Date(b.check_in + 'T12:00:00Z') - new Date(b.created_at.slice(0, 10) + 'T12:00:00Z')) / 86400000
    );
    if (lead < 0) continue;
    if (!byMonth.has(monthKey)) byMonth.set(monthKey, { sum: 0, count: 0 });
    const m = byMonth.get(monthKey);
    m.sum += lead;
    m.count += 1;
  }

  const months = MONTH_SHORT[lang] || MONTH_SHORT.en;
  const data = Array.from(byMonth.entries())
    .sort(([a], [b]) => a < b ? -1 : 1)
    .map(([key, { sum, count }]) => ({
      month: months[parseInt(key.slice(5, 7)) - 1],
      avg: Math.round(sum / count),
    }));

  if (data.length === 0) return <EmptyPanel text={t.analytics.empty} />;

  const overallAvg = data.length > 0
    ? Math.round(data.reduce((s, d) => s + d.avg, 0) / data.length)
    : null;

  return (
    <div style={panelStyle}>
      <PanelTitle>{t.analytics.leadTime.title}</PanelTitle>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
          <XAxis dataKey="month" stroke="#8B7D6B" tick={{ fontSize: 11 }} />
          <YAxis stroke="#8B7D6B" tick={{ fontSize: 11 }} allowDecimals={false} unit={t.analytics.leadTime.dayShort} />
          <Tooltip formatter={v => [`${v} ${t.analytics.leadTime.daysLabel}`, t.analytics.leadTime.avgLabel]} />
          <Bar dataKey="avg" fill="#2B6F8A">
            <LabelList dataKey="avg" position="top" style={{ fontSize: 11, fill: '#3D2B1F', fontWeight: 600 }} formatter={v => `${v}${t.analytics.leadTime.dayShort}`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {overallAvg != null && (
        <p style={{ fontSize: 12, color: '#8B7D6B', margin: 0, marginTop: 4 }}>
          {t.analytics.leadTime.mean.replace('{n}', overallAvg)}
        </p>
      )}
    </div>
  );
}

function PerLevelTable({ periodNights, rooms, period, breakdown, adrMode, t, cur, lang, roomTypeInfo }) {
  const days = daysInclusive(period.start, period.end);
  const groups = new Map();

  if (breakdown === 'room') {
    for (const r of rooms) groups.set(r.id, { key: r.id, label: r.name, roomCount: 1, nights: 0, net: 0, gross: 0 });
  } else {
    for (const r of rooms) {
      if (!groups.has(r.type)) groups.set(r.type, { key: r.type, label: resolveTypeLabel(r.type, roomTypeInfo, lang), roomCount: 0, nights: 0, net: 0, gross: 0 });
      groups.get(r.type).roomCount += 1;
    }
  }

  for (const n of periodNights) {
    const key = breakdown === 'room' ? n.roomId : n.roomType;
    const g = groups.get(key);
    if (!g) continue;
    g.nights += 1;
    g.net   += n.nightlyNet;
    g.gross += n.nightlyGross;
  }

  const rows = Array.from(groups.values()).map(g => ({
    ...g,
    occ: g.roomCount > 0 && days > 0 ? (g.nights / (g.roomCount * days)) * 100 : null,
    adr: g.nights > 0 ? (adrMode === 'net' ? g.net : g.gross) / g.nights : null,
  })).sort((a, b) => b.nights - a.nights);

  return (
    <div style={panelStyle}>
      <PanelTitle>{breakdown === 'room' ? t.analytics.breakdown.perRoom : t.analytics.breakdown.perRoomType}</PanelTitle>
      <p style={{ fontSize: 11, color: '#8B7D6B', margin: '0 0 12px' }}>{t.analytics.kpi.occupancy} · ADR</p>
      {rows.map(r => (
        <div key={r.key} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span style={{ color: '#3D2B1F', fontWeight: 600 }}>
              {r.label}
              {r.adr != null && (
                <span style={{ fontWeight: 400, color: '#8B7D6B', fontSize: 12, marginLeft: 8 }}>
                  {formatMoney(r.adr, cur)}
                </span>
              )}
            </span>
            <span style={{ color: '#5A7247', fontWeight: 700 }}>
              {r.occ == null ? '—' : `${r.occ.toFixed(0)}%`}
            </span>
          </div>
          <div style={{ height: 8, background: '#F5EFE6', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(r.occ || 0, 100)}%`, background: '#5A7247', borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function KpiCards({ roomNights, rooms, period, t, cur, adrMode, setAdrMode, lang }) {
  const days = daysInclusive(period.start, period.end);
  const revenue = sumRevenue(roomNights, 'net');
  const occ     = occupancyPct(roomNights, rooms, days);
  const adrVal  = adr(roomNights, adrMode);
  const otaCost = roomNights.reduce((s, n) => s + (n.nightlyOtaCost ?? 0), 0);
  const revpar = rooms.length > 0 && days > 0 ? revenue / (rooms.length * days) : null;
  const totalNights = roomNights.length;
  const possibleNights = rooms.length * days;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
      <Card title={t.analytics.kpi.revenue} value={formatMoney(revenue, cur)} accent="#5A7247" />
      <Card title={t.analytics.kpi.occupancy} value={occ == null ? '—' : `${occ.toFixed(1)}%`} accent="#3D2B1F"
        sub={occ != null && possibleNights > 0 ? `${totalNights} ${lang === 'bg' ? 'от' : 'of'} ${possibleNights} ${lang === 'bg' ? 'нощи' : 'nights'}` : null} />
      <div title={t.analytics.kpi.adrTip} style={{ background: '#FFFDF8', padding: '12px 14px', borderRadius: 12, border: '1px solid #E8D5B0', cursor: 'help' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#8B7D6B', textTransform: 'uppercase', letterSpacing: 1.6, fontFamily: "'Figtree', sans-serif" }}>{t.analytics.kpi.adr}</span>
          <span style={{ display: 'inline-flex', border: '1px solid #E8D5B0', borderRadius: 4, overflow: 'hidden', height: 18 }}>
            {['net', 'gross'].map(m => (
              <button key={m} onClick={() => setAdrMode(m)} style={{
                padding: '0 6px', fontSize: 9.5, border: 'none',
                background: adrMode === m ? '#3D2B1F' : 'transparent',
                color: adrMode === m ? '#C9A96E' : '#8B7D6B',
                cursor: 'pointer', fontWeight: 700, letterSpacing: 0.4,
                textTransform: 'uppercase', fontFamily: "'Figtree', sans-serif",
              }}>{t.analytics.kpi[m]}</button>
            ))}
          </span>
        </div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, color: '#3D2B1F', lineHeight: 1.05, fontVariantNumeric: 'lining-nums tabular-nums' }}>
          {adrVal == null ? '—' : formatMoney(adrVal, cur)}
        </div>
      </div>
      <Card title={t.analytics.kpi.otaCost} value={formatMoney(otaCost, cur)} accent="#C0562B" />
      <div style={{ gridColumn: 'span 2' }}>
        <Card
          title={t.analytics.kpi.revpar}
          value={revpar == null ? '—' : formatMoney(revpar, cur)}
          accent="#3D2B1F"
          tooltip={t.analytics.kpi.revparTip}
        />
      </div>
    </div>
  );
}

function Card({ title, value, accent, tooltip, sub }) {
  return (
    <div title={tooltip} style={{ background: '#FFFDF8', padding: '12px 14px', borderRadius: 12, border: '1px solid #E8D5B0', cursor: tooltip ? 'help' : undefined }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: '#8B7D6B', textTransform: 'uppercase', letterSpacing: 1.6, fontFamily: "'Figtree', sans-serif" }}>{title}</div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, color: accent, marginTop: 4, lineHeight: 1.05, fontVariantNumeric: 'lining-nums tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#8B7D6B', marginTop: 2, fontFamily: "'Figtree', sans-serif" }}>{sub}</div>}
    </div>
  );
}

function daysInclusive(startIso, endIso) {
  const a = new Date(startIso + 'T12:00:00Z');
  const b = new Date(endIso   + 'T12:00:00Z');
  return Math.round((b - a) / 86400000) + 1;
}

const selectStyle = { padding: '6px 10px', borderRadius: 6, border: '1px solid #8B7D6B', background: '#FFFDF8', color: '#3D2B1F', fontSize: 14 };
const fieldLbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#8B7D6B', marginTop: 8, marginBottom: 4 };
const btnPrimary   = { padding: '8px 16px', background: '#3D2B1F', color: '#FFFDF8', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnSecondary = { padding: '8px 16px', background: 'transparent', color: '#3D2B1F', border: '1px solid #8B7D6B', borderRadius: 6, cursor: 'pointer' };

// True only for a real calendar day-of-month. Rejects impossible markers like
// "09-31" or "02-30" that the plain regex would let through — these crash the
// Analytics page on Safari (Invalid Date → toISOString throws). Year 2001 is a
// non-leap reference, so 02-29 is rejected (ambiguous without a year).
function isRealMd(md) {
  if (!/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(md)) return false;
  const [m, d] = md.split('-').map(Number);
  return d <= new Date(2001, m, 0).getDate();
}

function formatSeason(startMd, endMd, lang) {
  startMd = startMd || '04-01';
  endMd   = endMd   || '10-31';
  const months = lang === 'bg'
    ? ['ян','фев','мар','апр','май','юни','юли','авг','сеп','окт','ное','дек']
    : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmt = md => {
    const [m, d] = md.split('-');
    return `${months[Number(m) - 1]} ${Number(d)}`;
  };
  return `${fmt(startMd)} – ${fmt(endMd)}`;
}

function isoAddYears(iso, n) {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCFullYear(d.getUTCFullYear() + n);
  return d.toISOString().slice(0, 10);
}
